/**
 * @typedef {Object} HistoryEntry
 * @property {string} url - The URL of the visited page
 * @property {string} name - The name or title of the visited page
 * @property {string} category - The type/category of the visited page
 * @property {string} timestamp - The ISO string timestamp of when the page was visited
 */

import Vue from 'vue'

const getDefaultState = () => {
  const history = typeof sessionStorage !== 'undefined' ? JSON.parse(sessionStorage.getItem('history')) : null
  return {
    size: 15, // Maximum number of history entries to keep
    history: history?.history || {}, // Object mapping trust slugs to arrays of HistoryEntry
    currentTrustSlug: history?.currentTrustSlug || null // Current trust context slug
  }
}

const state = getDefaultState()

export default {
  state,
  mutations: {
    setHistoryTrustSlug(state, slug) {
      state.currentTrustSlug = slug
    },
    /**
     * @param {Object} state
     * @param {HistoryEntry} entry
     */
    addEntry(state, entry) {
      if (!state.currentTrustSlug) {
        throw new Error('Trust slug must be set before adding history entries')
      }

      // Initialize history array for the current trust if it doesn't exist
      if (!state.history[state.currentTrustSlug]) {
        Vue.set(state.history, state.currentTrustSlug, [])
      }

      // Remove existing entry if it exists
      const existingIndex = state.history[state.currentTrustSlug].findIndex(e => e.url === entry.url)
      if (existingIndex !== -1) {
        state.history[state.currentTrustSlug].splice(existingIndex, 1)
      }

      // Add new entry to the front
      state.history[state.currentTrustSlug].unshift(entry)

      // Ensure history does not exceed the defined size
      if (state.history[state.currentTrustSlug].length > state.size) {
        state.history[state.currentTrustSlug].pop()
      }

      // Persist history to sessionStorage
      sessionStorage.setItem('history', JSON.stringify(state))
    }
  },
  actions: {
    setHistoryTrust({ commit }, trust) {
      // console.log('Setting history trust context to:', trust)
      if (typeof trust !== 'string') {
        throw new Error('Trust slug must be a string')
      }
      commit('setHistoryTrustSlug', trust)
    },
    /**
     * @param {Object} context
     * @param {HistoryEntry} entry
     */
    addHistoryEntry({ commit }, entry) {
      commit('addEntry', entry)
    }
  },
  getters: {
    /**
     * @param {Object} state
     * @returns {HistoryEntry[]}
     */
    activeHistory: state => {
      if (!state.currentTrustSlug) {
        return []
      }
      return state.history[state.currentTrustSlug] || []
    },
    hasHistory: state => {
      return (
        state.currentTrustSlug &&
        state.history[state.currentTrustSlug] &&
        state.history[state.currentTrustSlug].length > 0
      )
    }
  }
}
